import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import React, { useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { appConstants } from "../../config/app-constants";
import { EToastvariant, IRegistrationForm } from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import {
  authDataSelector,
  setTempRegisterData,
  unsetTempRegisterData
} from "../../redux/slice/AuthDataSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import FASvgIcon from "../FASvgIcon";
import ScrollToFirstError from "../ScrollToFirstError";
import PhoneInputField from "../fields/PhoneInputField";
import { LongContentModal } from "../modals";
import PasswordHintOverlay from "../overlay/PasswordHintOverlay";
import { ButtonSpinner } from "../placeholders";
import AuthApi from "./../../api/AuthApi";
import GoogleSignInBtn from "./GoogleSignInBtn";

const validationSchema = Yup.object().shape(
	{
		role: Yup.string()
			.oneOf(["ca", "user"], "Please accept Terms of Service")
			.required("Please accept Terms of Service"),
		firstName: Yup.string()
			.trim()
			.min(3, "Please enter at least 3 characters")
			.max(255, "First Name must be less than 255 characters")
			.matches(appConstants.NAME_REGEX, "Invalid First Name")
			.required("First Name is required"),
		middleName: Yup.string().when("middleName", ([middleName], schema) => {
			return middleName
				? schema
						.trim()
						.min(3, "Please enter at least 3 characters")
						.max(255, "Middle Name must be less than 255 characters")
						.matches(appConstants.NAME_REGEX, "Invalid Middle Name")
				: schema.trim().notRequired();
		}),
		lastName: Yup.string()
			.trim()
			.min(3, "Please enter at least 3 characters")
			.max(255, "Last Name must be less than 255 characters")
			.matches(appConstants.NAME_REGEX, "Invalid Last Name")
			.required("Last Name is required"),
		email: Yup.string()
			.trim()
			.email("Please enter a valid email")
			.matches(appConstants.EMAIL_REGEX, "Please enter a valid email")
			.required("Email is required"),
		phoneNumber: Yup.string()
			.trim()
			.min(13, "Please enter valid phone number") // including country code +91
			.max(13, "Please enter valid phone number") // including country code +91
			.required("Phone number is required"),
		password: Yup.string()
			.trim()
			.matches(appConstants.PASSWORD_REGEX, "Invalid password formate")
			.required("Password is required"),
		confirmPassword: Yup.string()
			.trim()
			.oneOf([Yup.ref("password")], "Passwords must match")
			.required("Confirm Password is required"),
		terms_accepted: Yup.boolean()
			.oneOf([true], "Please accept Terms of Service")
			.required("Please accept Terms of Service")
	},
	[["middleName", "middleName"]]
);

const RegisterForm: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { tempRegisterData } = useAppSelector(authDataSelector);

	const pwdOverlayTargetRef = useRef<HTMLInputElement | null>(null);

	const [showHintOverlay, setShowHintOverlay] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState<boolean>(false);
	const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState<boolean>(false);
	const [showCookiesPolicyModal, setShowCookiesPolicyModal] = useState<boolean>(false);

	const initialValues: IRegistrationForm = {
		role: "user",
		firstName: tempRegisterData.firstName || "",
		middleName: tempRegisterData.middleName || "",
		lastName: tempRegisterData.lastName || "",
		phoneNumber: tempRegisterData.phoneNumber || "",
		email: tempRegisterData.email || "",
		password: "",
		confirmPassword: "",
		terms_accepted: false
	};

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const toggleShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const handleRegisterFormSubmit = async (
		values: IRegistrationForm,
		{ setSubmitting, resetForm }: FormikHelpers<IRegistrationForm>
	) => {
		try {
			setSubmitting(true);
			dispatch(setTempRegisterData(values));

			const registerData = {
				role: values.role,
				firstName: values.firstName,
				middleName: values.middleName,
				lastName: values.lastName,
				phoneNumber: values.phoneNumber,
				email: values.email.toLowerCase(),
				password: values.password
				// confirmPassword: values.confirmPassword,
				// terms_accepted: values.terms_accepted
			};
			const authApi = AuthApi.getInstance();
			const registerResp = await authApi.register(registerData);

			await StorageService.setPreVerifyData(
				JSON.stringify({
					email: values.email.toLowerCase(),
					preVerificationToken: registerResp.preVerificationToken
				})
			);
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Success",
					message: "OTP sent successfully",
					variant: EToastvariant.Success
				})
			);

			navigate("/verification", {
				state: { fromScreen: "register", showVerification: true }
			});
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Container>
				<div className="auth-form-wrap two-col-form-wrap">
					<Row className="align-items-center justify-content-md-center">
						<Col>
							<Card bg="white" className="auth-card">
								<Card.Body>
									<h1 className="fw-bold text-center mb-1">Create your Account</h1>
									<p className="text-center text-greyDark">to continue to tax filing dashboard</p>
									<Formik
										initialValues={initialValues}
										validationSchema={validationSchema}
										onSubmit={handleRegisterFormSubmit}
									>
										{({
											handleChange,
											handleBlur,
											handleSubmit,
											validateForm,
											values,
											errors,
											touched,
											isValid,
											submitCount,
											isSubmitting,
											setFieldValue
										}) => (
											<>
												<Form
													onSubmit={async (event) => {
														event.preventDefault();
														await validateForm();
														handleSubmit();
													}}
												>
													<ScrollToFirstError
														submitCount={submitCount}
														isValid={isValid}
														errors={errors}
													/>
													<GoogleSignInBtn
														route={"/register-successful"}
														buttonTitle={"Sign Up with Google"}
													/>
													<Row>
														<Col xs={12} lg={4}>
															<Form.Group className="form-group" controlId="firstName">
																<Form.Label className="required-field">First Name</Form.Label>
																<Form.Control
																	type="text"
																	autoComplete="false"
																	placeholder="John"
																	maxLength={255}
																	name="firstName"
																	value={values.firstName}
																	onBlur={handleBlur}
																	onChange={(e) => {
																		const text = e.target.value;
																		const inputString = text
																			.replace(/\.{2,}/g, ".")
																			.replace(/\s+/g, " ");
																		setFieldValue("firstName", inputString);
																	}}
																	isInvalid={Boolean(errors.firstName) && touched.firstName}
																/>
																<Form.Control.Feedback type="invalid">
																	{errors.firstName}
																</Form.Control.Feedback>
															</Form.Group>
														</Col>
														<Col xs={12} lg={4}>
															<Form.Group className="form-group" controlId="middleName">
																<Form.Label>Middle Name</Form.Label>
																<Form.Control
																	type="text"
																	autoComplete="false"
																	placeholder="Paul"
																	maxLength={255}
																	name="middleName"
																	value={values.middleName}
																	onBlur={handleBlur}
																	onChange={(e) => {
																		const text = e.target.value;
																		const inputString = text
																			.replace(/\.{2,}/g, ".")
																			.replace(/\s+/g, " ");
																		setFieldValue("middleName", inputString);
																	}}
																	isInvalid={Boolean(errors.middleName) && touched.middleName}
																/>
																<Form.Control.Feedback type="invalid">
																	{errors.middleName}
																</Form.Control.Feedback>
															</Form.Group>
														</Col>
														<Col xs={12} lg={4}>
															<Form.Group className="form-group" controlId="lastName">
																<Form.Label className="required-field">Last Name</Form.Label>
																<Form.Control
																	type="text"
																	autoComplete="false"
																	placeholder="Walker"
																	maxLength={255}
																	name="lastName"
																	value={values.lastName}
																	onBlur={handleBlur}
																	onChange={(e) => {
																		const text = e.target.value;
																		const inputString = text
																			.replace(/\.{2,}/g, ".")
																			.replace(/\s+/g, " ");
																		setFieldValue("lastName", inputString);
																	}}
																	isInvalid={Boolean(errors.lastName) && touched.lastName}
																/>
																<Form.Control.Feedback type="invalid">
																	{errors.lastName}
																</Form.Control.Feedback>
															</Form.Group>
														</Col>

														<Col xs={12} lg={6}>
															<Form.Group className="form-group" controlId="email">
																<Form.Label className="required-field">Email</Form.Label>
																<Form.Control
																	type="email"
																	autoComplete="false"
																	placeholder="Please enter your email"
																	maxLength={255}
																	name="email"
																	value={values.email}
																	onBlur={handleBlur}
																	onChange={handleChange}
																	isInvalid={Boolean(errors.email) && touched.email}
																/>
																<Form.Control.Feedback type="invalid">
																	{errors.email}
																</Form.Control.Feedback>
															</Form.Group>
														</Col>
														<Col xs={12} lg={6}>
															<Form.Group className="form-group" controlId="phoneNumber">
																<Form.Label className="required-field">Phone</Form.Label>
																<PhoneInputField
																	name="phoneNumber"
																	value={values.phoneNumber}
																	onChange={(value) => {
																		setFieldValue("phoneNumber", value);
																	}}
																	onBlur={handleBlur("phoneNumber")}
																	isInvalid={Boolean(errors.phoneNumber) && touched.phoneNumber}
																	errorMsg={errors.phoneNumber}
																	formType="GENERAL"
																/>
															</Form.Group>
														</Col>

														<Col xs={12} lg={6}>
															<Form.Group className="form-group" controlId="password">
																<Form.Label className="required-field">Password</Form.Label>
																<div className="field-has-icon" ref={pwdOverlayTargetRef}>
																	<Form.Control
																		type={showPassword ? "text" : "password"}
																		autoComplete="false"
																		placeholder="***********"
																		name="password"
																		value={values.password}
																		onChange={handleChange}
																		isInvalid={Boolean(errors.password) && touched.password}
																		onFocus={() => {
																			setShowHintOverlay(true);
																		}}
																		onBlur={(e) => {
																			handleBlur(e);
																			setShowHintOverlay(false);
																		}}
																	/>
																	<Form.Control.Feedback type="invalid">
																		{errors.password}
																	</Form.Control.Feedback>
																	<Button
																		variant="transparent"
																		className="field-icon"
																		onClick={toggleShowPassword}
																	>
																		<FASvgIcon icon={showPassword ? faEye : faEyeSlash} />
																	</Button>
																</div>

																<PasswordHintOverlay
																	show={showHintOverlay}
																	targetRef={pwdOverlayTargetRef}
																/>
															</Form.Group>
														</Col>
														<Col xs={12} lg={6}>
															<Form.Group className="form-group" controlId="confirmPassword">
																<Form.Label className="required-field">Confirm Password</Form.Label>
																<div className="field-has-icon">
																	<Form.Control
																		type={showConfirmPassword ? "text" : "password"}
																		autoComplete="false"
																		placeholder="***********"
																		name="confirmPassword"
																		value={values.confirmPassword}
																		onBlur={handleBlur}
																		onChange={handleChange}
																		isInvalid={
																			Boolean(errors.confirmPassword) && touched.confirmPassword
																		}
																	/>
																	<Form.Control.Feedback type="invalid">
																		{errors.confirmPassword}
																	</Form.Control.Feedback>
																	<Button
																		variant="transparent"
																		className="field-icon"
																		onClick={toggleShowConfirmPassword}
																	>
																		<FASvgIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
																	</Button>
																</div>
															</Form.Group>
														</Col>

														<Col xs={12}>
															<Form.Group controlId="terms_accepted" className="form-group">
																<Form.Check role="button" type="checkbox" id="terms_accepted">
																	<Form.Check.Input
																		type="checkbox"
																		name="terms_accepted"
																		checked={values.terms_accepted}
																		onChange={handleChange}
																		isInvalid={
																			Boolean(errors.terms_accepted) && touched.terms_accepted
																		}
																	/>
																	<Form.Check.Label>
																		By Clicking Sign Up you agree to Mytax Club.{" "}
																		<Link
																			className="text-primary"
																			onClick={() => setShowTermsOfServiceModal(true)}
																			to={""}
																		>
																			User Agreement
																		</Link>
																		,{" "}
																		<Link
																			className="text-primary"
																			onClick={() => setShowPrivacyPolicyModal(true)}
																			to={""}
																		>
																			Privacy Policy
																		</Link>{" "}
																		&{" "}
																		<Link
																			className="text-primary"
																			onClick={() => setShowCookiesPolicyModal(true)}
																			to={""}
																		>
																			Cookie Policy
																		</Link>
																		.
																	</Form.Check.Label>
																	<Form.Control.Feedback type="invalid">
																		{errors.terms_accepted}
																	</Form.Control.Feedback>
																</Form.Check>
															</Form.Group>
														</Col>
													</Row>

													<div className="btn-wrap">
														<Button
															variant="primary"
															type="submit"
															size="lg"
															className="w-100 shadow"
															disabled={isSubmitting}
														>
															{!isSubmitting ? "Sign Up" : <ButtonSpinner />}
														</Button>
													</div>
												</Form>

												<LongContentModal
													showModal={showTermsOfServiceModal}
													title="Terms of Use"
													onYesBtnText="Accept"
													onYesBtnClick={() => {
														setFieldValue("terms_accepted", true);
														setShowTermsOfServiceModal(false);
													}}
													actionKey={"termsOfService"}
												/>

												<LongContentModal
													showModal={showPrivacyPolicyModal}
													title="Privacy Policy"
													onYesBtnText="Close"
													onYesBtnClick={() => {
														setShowPrivacyPolicyModal(false);
													}}
													actionKey={"privacyPolicy"}
												/>

												<LongContentModal
													showModal={showCookiesPolicyModal}
													title="Cookies Policy"
													onYesBtnText="Close"
													onYesBtnClick={() => {
														setShowCookiesPolicyModal(false);
													}}
													actionKey={"cookiesPolicy"}
												/>
											</>
										)}
									</Formik>
								</Card.Body>
							</Card>

							<div className="auth-footer-1 justify-content-center">
								<Form.Text className="m-0 text-greyDark">
									Already have a My Tax Club account?
								</Form.Text>
								<Button
									variant="outline-greyDark"
									size="sm"
									onClick={() => {
										dispatch(unsetTempRegisterData());
										navigate("/");
									}}
								>
									Sign In
								</Button>
							</div>
						</Col>
					</Row>
				</div>
			</Container>
		</>
	);
};
export default RegisterForm;
